// URL state contract for /intel.
//
// One filter state, three views, zero re-fetch on switch (per the design plan,
// `doc/intel-page-design-plan.md` line 28). This module is the single source of
// truth for serializing the Intel page's working state into URL search params
// and back again so that a reload, a copy-paste, or a back/forward navigation
// preserves what the analyst was looking at.
//
// Design decisions:
//
//   * Compact, human-readable params — `?view=map&viewport=-77.04,38.91,5.3
//     &severity_bucket=HOT,HIGH&source=OFAC,ACLED&asof=2026-05-18T14:00:00Z`.
//     Not `?state=<base64-json-blob>`. URLs in emails and Slack need to be
//     readable + diff-able.
//
//   * Per-field encoding driven by `intelFilterSchema` field types — adding a
//     new field to the schema requires no change here unless it uses a brand-
//     new FilterFieldType. (Iron Rule 11: one filter contract.)
//
//   * Defensive parsing — malformed value → drop that field, keep the rest.
//     Unknown params → ignore. Reload must never crash on a hand-edited URL.
//
//   * Stable hashing — `intelStateHash` is a deterministic JSON of the state
//     with sorted keys; identical states produce identical hashes regardless
//     of property-insertion order. Used as a TanStack Query key so view
//     switches that don't change state share the cached result (zero re-fetch).
//
// Extensibility note for H4 (layer registry zoom thresholds): when layer
// visibility joins URL state, add a `layers: string[]` field to `IntelUrlState`
// and a `layers` param encoded as CSV. The current shape leaves room for that
// without a breaking refactor.

import {
  filters as f,
  type filters as ft,
  intelDomains as iDom,
  intel as ic,
  type intel as it,
} from '@eidollon/contracts';

/* ──────────────────────────────────────────────────────────────────────────
 *  Types
 * ────────────────────────────────────────────────────────────────────────── */

/** A geographic viewport — lng/lat center and zoom. Bounds are RFC-7946 lng
 *  ∈ [-180,180], lat ∈ [-90,90] and MapLibre zoom ∈ [0,22]. */
export interface IntelViewport {
  lng: number;
  lat: number;
  zoom: number;
}

/** The full URL-state of the Intel page. Every field is optional in the
 *  parsed form — defaults apply when serializing for the wire. */
export interface IntelUrlState {
  /** Active view tab. */
  view: it.IntelView;
  /** Optional camera position. `undefined` = let the map pick a default. */
  viewport: IntelViewport | undefined;
  /** Filter selection. Keys match `intelFilterSchema.fields[*].id`. */
  filters: ft.FilterState;
  /** Time-scrubber position. ISO timestamp when paused, `'live'` otherwise. */
  asOf: string | 'live';
  /** Single AOI polygon — `undefined` when no AOI is set. Multi-AOI users
   *  see only the first polygon round-trip through the URL today; H4 may
   *  generalize to `?aoi=p1|p2|p3`. The full multi-polygon list still lives
   *  in `filters.aoi` for in-app state. */
  aoi: GeoJSON.Polygon | undefined;
  /** Currently-pinned signal id (open in `EntityPopup`). */
  selectedSignalId: string | undefined;
}

export const INTEL_URL_STATE_DEFAULT: IntelUrlState = Object.freeze({
  view: 'map',
  viewport: undefined,
  filters: {},
  asOf: 'live',
  aoi: undefined,
  selectedSignalId: undefined,
}) as IntelUrlState;

/* ──────────────────────────────────────────────────────────────────────────
 *  URL parameter names
 *
 *  Reserved top-level keys; everything else in the URL is treated as a
 *  filter field id and looked up in `intelFilterSchema`.
 * ────────────────────────────────────────────────────────────────────────── */

const PARAM_VIEW = 'view';
const PARAM_VIEWPORT = 'viewport';
const PARAM_ASOF = 'asof';
const PARAM_AOI = 'aoi';
const PARAM_SIGNAL = 'signal';

const RESERVED_PARAMS = new Set<string>([
  PARAM_VIEW,
  PARAM_VIEWPORT,
  PARAM_ASOF,
  PARAM_AOI,
  PARAM_SIGNAL,
]);

/** Schema field-id index, computed once. Maps field id → field spec. */
const FIELD_INDEX: ReadonlyMap<string, ft.FilterField> = new Map(
  f.intelFilterSchema.fields.map((field) => [field.id, field]),
);

/* ──────────────────────────────────────────────────────────────────────────
 *  Parse — URL → IntelUrlState
 * ────────────────────────────────────────────────────────────────────────── */

/** Parse a URL search string (or pre-built `URLSearchParams`) into a partial
 *  `IntelUrlState`. Unknown params are ignored; malformed values are dropped
 *  silently per-field so a single bad input never wipes the whole state. */
export function parseIntelUrlState(search: string | URLSearchParams): Partial<IntelUrlState> {
  const params = search instanceof URLSearchParams ? search : new URLSearchParams(search);
  const out: Partial<IntelUrlState> = {};

  // view
  const view = parseEnumParam(params.get(PARAM_VIEW), ic.IntelView.options);
  if (view) out.view = view;

  // viewport
  const viewport = parseViewport(params.get(PARAM_VIEWPORT));
  if (viewport) out.viewport = viewport;

  // asOf
  const asOf = parseAsOf(params.get(PARAM_ASOF));
  if (asOf !== undefined) out.asOf = asOf;

  // aoi (single GeoJSON polygon ring)
  const aoi = parseAoi(params.get(PARAM_AOI));
  if (aoi) out.aoi = aoi;

  // selected signal id (unicode-safe; URLSearchParams already decodes)
  const sig = params.get(PARAM_SIGNAL);
  if (sig && sig.length > 0 && sig.length <= 256) out.selectedSignalId = sig;

  // filters — every non-reserved param is treated as a candidate filter field
  const filters: ft.FilterState = {};
  for (const [key, raw] of params.entries()) {
    if (RESERVED_PARAMS.has(key)) continue;
    if (raw == null || raw.length === 0) continue;
    const field = FIELD_INDEX.get(key);
    if (!field) continue; // unknown filter field → drop
    const parsed = parseFilterValue(field, raw);
    if (parsed !== undefined) filters[key] = parsed;
  }
  if (Object.keys(filters).length > 0) out.filters = filters;

  return out;
}

/* ──────────────────────────────────────────────────────────────────────────
 *  Serialize — IntelUrlState → URL
 * ────────────────────────────────────────────────────────────────────────── */

/** Serialize an `IntelUrlState` into a `URLSearchParams`. Defaults
 *  (view=map, asOf=live, empty filters) are omitted to keep the URL minimal.
 *
 *  When `existing` is passed, params **not owned** by IntelUrlState (i.e.
 *  unknown to RESERVED_PARAMS + the intel filter schema) are preserved.
 *  That lets TanStack-Router-managed siblings (sort, group, …) coexist
 *  with our `history.replaceState` writes. */
export function intelStateToUrl(state: IntelUrlState, existing?: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams();

  if (existing) {
    for (const [key, value] of existing.entries()) {
      if (RESERVED_PARAMS.has(key)) continue;
      if (FIELD_INDEX.has(key)) continue;
      params.set(key, value);
    }
  }

  if (state.view !== 'map') params.set(PARAM_VIEW, state.view);

  if (state.viewport) {
    params.set(PARAM_VIEWPORT, encodeViewport(state.viewport));
  }

  if (state.asOf && state.asOf !== 'live') params.set(PARAM_ASOF, state.asOf);

  if (state.aoi) {
    const encoded = encodePolygon(state.aoi);
    if (encoded) params.set(PARAM_AOI, encoded);
  }

  if (state.selectedSignalId) params.set(PARAM_SIGNAL, state.selectedSignalId);

  // Filter fields — iterate the schema for stable param order (sorted by
  // field-id index, which matches insertion order in the schema).
  for (const field of f.intelFilterSchema.fields) {
    const value = state.filters[field.id];
    if (value === undefined) continue;
    const encoded = encodeFilterValue(field, value);
    if (encoded != null && encoded.length > 0) params.set(field.id, encoded);
  }

  return params;
}

/* ──────────────────────────────────────────────────────────────────────────
 *  Hierarchical signal_type write-back (BUG-1)
 *
 *  The Intel rail's HierarchicalFilter only ever speaks in the subcategory
 *  keys that have a 1:1 wire code (the values of SUBCATEGORY_TO_SIGNAL_TYPE —
 *  SAN/SEI/WEA/FIR/SUP). But `filterState.signal_type` can also hold codes the
 *  rail can't represent (CFL/POL/OBL/AVI/ANO/NEW), put there by the map legend
 *  toggle, the chip composer, or a hand-edited URL.
 *
 *  Rebuilding `signal_type` purely from the rail's selection silently dropped
 *  those non-rail codes. The visible symptom: after the legend wrote an
 *  "all-except-X" enumeration (which contains CFL/POL/…), toggling a rail
 *  subcat off corrupted the selection and a deselect failed to return to the
 *  unfiltered state — "filters, when selected and deselected, still show".
 *
 *  This helper is the round-trip-safe write-back: preserve every code the rail
 *  does NOT own, then replace only the rail-owned codes with the codes for the
 *  currently selected subcategories. Selecting then deselecting the same subcat
 *  returns `signal_type` to its prior value (empty → unfiltered). Pure +
 *  unit-tested so the round-trip can't silently regress.
 * ────────────────────────────────────────────────────────────────────────── */

/** Compute the next `signal_type` code list when the hierarchical rail's
 *  selection changes to `selectedSubcats`. `currentSignalType` is the current
 *  raw `filterState.signal_type` (string | string[] | undefined). Returns the
 *  de-duplicated code list (empty array = clear the field). */
export function nextSignalTypeFromSubcats(
  currentSignalType: ft.FilterValue | undefined,
  selectedSubcats: ReadonlySet<string>,
): string[] {
  const codeBySubcat = iDom.SUBCATEGORY_TO_SIGNAL_TYPE;
  const railOwnedCodes = new Set<string>(Object.values(codeBySubcat));

  const currentCodes: string[] = Array.isArray(currentSignalType)
    ? currentSignalType.filter((x): x is string => typeof x === 'string')
    : typeof currentSignalType === 'string' && currentSignalType.length > 0
      ? [currentSignalType]
      : [];

  // Codes the rail doesn't manage — preserve verbatim.
  const preserved = currentCodes.filter((c) => !railOwnedCodes.has(c));

  // Codes for the rail's current selection.
  const railSelected: string[] = [];
  for (const sub of selectedSubcats) {
    const code = codeBySubcat[sub as keyof typeof codeBySubcat];
    if (code) railSelected.push(code);
  }

  return Array.from(new Set([...preserved, ...railSelected]));
}

/* ──────────────────────────────────────────────────────────────────────────
 *  Hash — stable, sort-key-canonical JSON for TanStack Query keys
 * ────────────────────────────────────────────────────────────────────────── */

/** Deterministic string representation of the full state. Two states that
 *  encode to the same URL produce the same hash; reordering object keys does
 *  not change the result. Cheap to compute and a fine cache-key. */
export function intelStateHash(state: IntelUrlState): string {
  return stableStringify(state);
}

/* ──────────────────────────────────────────────────────────────────────────
 *  Per-field encoding
 *
 *  Strategy by `FilterFieldType`:
 *
 *    multi-enum / async-enum  →  CSV ("OFAC,ACLED")
 *    enum                     →  raw string
 *    text                     →  raw string
 *    bool                     →  "1" | "0"
 *    number-range             →  "min:max" (either side may be blank)
 *    money-range              →  "min:max[:CUR]"
 *    date-range               →  "from:to" or "since=24h" form (relative)
 *    geo-polygon              →  delegated to top-level `aoi=` param;
 *                                 inline param omitted (avoids redundancy)
 * ────────────────────────────────────────────────────────────────────────── */

function parseFilterValue(field: ft.FilterField, raw: string): ft.FilterValue | undefined {
  switch (field.type) {
    case 'text':
    case 'enum':
      return raw;
    case 'multi-enum':
    case 'async-enum': {
      const parts = raw
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      return parts.length > 0 ? parts : undefined;
    }
    case 'bool':
      if (raw === '1' || raw === 'true') return true;
      if (raw === '0' || raw === 'false') return false;
      return undefined;
    case 'number-range': {
      const [minStr, maxStr] = raw.split(':');
      const min = parseFiniteNumber(minStr);
      const max = parseFiniteNumber(maxStr);
      if (min === undefined && max === undefined) return undefined;
      const value: { min?: number; max?: number } = {};
      if (min !== undefined) value.min = min;
      if (max !== undefined) value.max = max;
      return value;
    }
    case 'money-range': {
      const [minStr, maxStr, cur] = raw.split(':');
      const min = parseFiniteNumber(minStr);
      const max = parseFiniteNumber(maxStr);
      if (min === undefined && max === undefined && !cur) return undefined;
      const value: { min?: number; max?: number; currency?: string } = {};
      if (min !== undefined && min >= 0) value.min = min;
      if (max !== undefined && max >= 0) value.max = max;
      if (cur && /^[A-Z]{2}$/.test(cur)) value.currency = cur;
      return value;
    }
    case 'date-range': {
      // `from:to` (each side optional). Both blank → undefined.
      const [from, to] = raw.split(':');
      const value: { from?: string; to?: string } = {};
      if (from && from.length >= 8) value.from = from;
      if (to && to.length >= 8) value.to = to;
      if (!value.from && !value.to) return undefined;
      return value;
    }
    case 'geo-polygon':
      // Owned by top-level `aoi=` param. Don't double-encode.
      return undefined;
  }
}

function encodeFilterValue(field: ft.FilterField, value: ft.FilterValue): string | undefined {
  switch (field.type) {
    case 'text':
    case 'enum':
      return typeof value === 'string' ? value : undefined;
    case 'multi-enum':
    case 'async-enum':
      return Array.isArray(value) && value.length > 0
        ? value.filter((v): v is string => typeof v === 'string').join(',')
        : undefined;
    case 'bool':
      return typeof value === 'boolean' ? (value ? '1' : '0') : undefined;
    case 'number-range': {
      const v = value as { min?: number | null; max?: number | null };
      if (!v || typeof v !== 'object') return undefined;
      const min = typeof v.min === 'number' ? String(v.min) : '';
      const max = typeof v.max === 'number' ? String(v.max) : '';
      if (!min && !max) return undefined;
      return `${min}:${max}`;
    }
    case 'money-range': {
      const v = value as { min?: number | null; max?: number | null; currency?: string };
      if (!v || typeof v !== 'object') return undefined;
      const min = typeof v.min === 'number' ? String(v.min) : '';
      const max = typeof v.max === 'number' ? String(v.max) : '';
      const cur = v.currency ?? '';
      if (!min && !max && !cur) return undefined;
      return cur ? `${min}:${max}:${cur}` : `${min}:${max}`;
    }
    case 'date-range': {
      const v = value as { from?: string | null; to?: string | null };
      if (!v || typeof v !== 'object') return undefined;
      const from = v.from ?? '';
      const to = v.to ?? '';
      if (!from && !to) return undefined;
      return `${from}:${to}`;
    }
    case 'geo-polygon':
      return undefined;
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 *  Helpers — small, inline, no abstractions
 * ────────────────────────────────────────────────────────────────────────── */

function parseEnumParam<T extends string>(
  value: string | null,
  allowed: readonly T[],
): T | undefined {
  if (!value) return undefined;
  return allowed.includes(value as T) ? (value as T) : undefined;
}

function clamp(n: number, min: number, max: number): number {
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

function parseFiniteNumber(s: string | undefined | null): number | undefined {
  if (s == null || s === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

function parseViewport(raw: string | null): IntelViewport | undefined {
  if (!raw) return undefined;
  const parts = raw.split(',');
  if (parts.length !== 3) return undefined;
  const lng = parseFiniteNumber(parts[0]);
  const lat = parseFiniteNumber(parts[1]);
  const zoom = parseFiniteNumber(parts[2]);
  if (lng === undefined || lat === undefined || zoom === undefined) return undefined;
  // Drop completely out-of-range values (likely a hand-edit error) rather
  // than silently snapping the camera to (180, 90) — better to lose the
  // viewport and let the default kick in.
  if (lng < -180 || lng > 180) return undefined;
  if (lat < -90 || lat > 90) return undefined;
  return {
    lng,
    lat,
    zoom: clamp(zoom, 0, 22),
  };
}

function encodeViewport(v: IntelViewport): string {
  // 4 decimal places ≈ 11m precision — plenty for a deep-link camera target,
  // and keeps the URL short. Zoom rounds to 2dp (sub-tile granularity).
  return [round(v.lng, 4), round(v.lat, 4), round(v.zoom, 2)].join(',');
}

function parseAsOf(raw: string | null): IntelUrlState['asOf'] | undefined {
  if (!raw) return undefined;
  if (raw === 'live') return 'live';
  // ISO-8601 sniff — minimum `YYYY-MM-DD` (10 chars). `Date.parse` accepts a
  // lot; we round-trip through `toISOString` so any garbage produces NaN and
  // gets dropped.
  if (raw.length < 10) return undefined;
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return undefined;
  return new Date(ms).toISOString();
}

function parseAoi(raw: string | null): GeoJSON.Polygon | undefined {
  if (!raw) return undefined;
  // Format: `lng,lat;lng,lat;lng,lat;...` — single linear ring.
  const points: number[][] = [];
  for (const pair of raw.split(';')) {
    if (!pair) continue;
    const [lngStr, latStr] = pair.split(',');
    const lng = parseFiniteNumber(lngStr);
    const lat = parseFiniteNumber(latStr);
    if (lng === undefined || lat === undefined) return undefined; // malformed → drop
    if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return undefined;
    points.push([lng, lat]);
  }
  if (points.length < 3) return undefined; // needs ≥3 distinct verts for a ring
  // Close the ring if the user didn't repeat the first point at the end.
  const first = points[0];
  const last = points[points.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    points.push([first[0]!, first[1]!]);
  }
  return { type: 'Polygon', coordinates: [points] };
}

function encodePolygon(p: GeoJSON.Polygon): string | undefined {
  const ring = p.coordinates[0];
  if (!ring || ring.length < 3) return undefined;
  // Drop the closing point if it duplicates the first (round-trip stable).
  const verts =
    ring.length > 3 &&
    ring[0]?.[0] === ring[ring.length - 1]?.[0] &&
    ring[0]?.[1] === ring[ring.length - 1]?.[1]
      ? ring.slice(0, -1)
      : ring;
  return verts.map((pt) => `${round(pt[0]!, 4)},${round(pt[1]!, 4)}`).join(';');
}

function round(n: number, places: number): number {
  const m = 10 ** places;
  return Math.round(n * m) / m;
}

/** Stable JSON: keys sorted at every level, undefined dropped. Mirrors
 *  `packages/ui-filters/src/url.ts#stableStringify` but inlined here so this
 *  module has no inter-package dependency beyond `@eidollon/contracts`. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const parts: string[] = [];
  for (const k of keys) {
    const v = obj[k];
    if (v === undefined) continue;
    parts.push(`${JSON.stringify(k)}:${stableStringify(v)}`);
  }
  return `{${parts.join(',')}}`;
}
